import React, { useState, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { formatDistanceToNow } from "date-fns";
import { cleaningApiBaseUrlFromEnv } from "../../runtime/loadSharedDashboardConfig";
import { authenticatedFetch } from "../../api/attachBearerToken";

interface CameraStat {
  sensor_id: string;
  camera_name: string;
  location_name: string;
  location_id?: string;
  coverage: string;
  status: string;
  last_cleaned: string;
  image_link?: string | null;
}

interface CleaningCoverageBarsProps {
  date: string;
}

const CleaningCoverageBars: React.FC<CleaningCoverageBarsProps> = ({ date }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [cameras, setCameras] = useState<CameraStat[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<CameraStat | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchTopCoverage = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        const response = await authenticatedFetch(
          `${cleaningApiBaseUrlFromEnv()}/cleaning/top-coverage?${params.toString()}`,
          { signal: controller.signal },
        );
        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data)
            ? data
            : Array.isArray((data as any)?.data)
              ? (data as any).data
              : data && typeof data === "object"
                ? [data]
                : [];
          setCameras(items);
          setSelectedCamera(null);
        }
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          console.error("Failed to fetch top coverage:", error);
          setCameras([]);
          setSelectedCamera(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchTopCoverage();
    return () => controller.abort();
  }, [date]);

  const getFormattedTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const dateObj = new Date(dateString);
      const relative = formatDistanceToNow(dateObj, { addSuffix: true });
      return relative.startsWith("in ") ? relative.replace("in ", "") + " ago" : relative;
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-6">
      <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Camera Coverage Analysis</h2>
      <div className={`rounded-2xl p-6 ${isDarkMode ? "bg-[#1C202F]/50 border border-gray-800" : "bg-white border border-gray-100 shadow-sm"}`}>
        {loading && cameras.length === 0 ? (
          <div className="h-64 flex items-center justify-center">
            <p className={isDarkMode ? "text-gray-500" : "text-gray-400"}>Loading...</p>
          </div>
        ) : cameras.length === 0 ? (
          <div className={`h-64 flex items-center justify-center rounded-xl ${isDarkMode ? "bg-gray-800/20" : "bg-gray-50"}`}>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>No camera data available.</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            {selectedCamera && (
              <div
                className={`w-full lg:w-1/2 flex flex-col items-center justify-center p-8 rounded-2xl ${isDarkMode ? "bg-[#0b0d14]/50 border border-gray-800" : "bg-gray-50/50"}`}
              >
                <h3 className={`text-xl font-bold mb-6 text-center ${isDarkMode ? "text-white" : "text-gray-800"}`}>{selectedCamera.camera_name}</h3>

                <div className="flex flex-col items-center gap-4 text-center">
                  {selectedCamera.location_name && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>Location:</span>
                      <span className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{selectedCamera.location_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-8">
                    <div className="flex flex-col items-center">
                      <span className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Coverage</span>
                      <span className={`text-3xl font-black ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`}>{selectedCamera.coverage}</span>
                    </div>
                    <div className={`w-px h-10 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`} />
                    <div className="flex flex-col items-center">
                      <span className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Status</span>
                      <span className={`text-3xl font-black ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>{selectedCamera.status}</span>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-800 w-full flex justify-center">
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>
                      Last updated: {getFormattedTime(selectedCamera.last_cleaned)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className={`w-full ${selectedCamera ? "lg:w-1/2" : ""} flex flex-col gap-4 overflow-y-auto max-h-[500px] pr-2 custom-scrollbar`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>Cameras List</h3>
                {selectedCamera && (
                  <button
                    onClick={() => setSelectedCamera(null)}
                    className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                      isDarkMode ? "bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white shadow-sm" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:text-gray-900 shadow-sm"
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-3">
                {/* Grid table layout - fixed columns keep coverage % aligned regardless of status badge width */}
                {cameras.map((camera, index) => {
                  const isSelected = selectedCamera?.sensor_id === camera.sensor_id;

                  return (
                    <div
                      key={camera.sensor_id || index}
                      onClick={() => setSelectedCamera(isSelected ? null : camera)}
                      className={`rounded-xl cursor-pointer transition-all duration-200 border ${
                        isSelected
                          ? isDarkMode
                            ? "bg-white/5 border-white/10 p-3 shadow-lg"
                            : "bg-gray-50 border-gray-200 p-3"
                          : isDarkMode
                            ? "border-transparent p-3 hover:bg-white/5"
                            : "border-transparent p-3 hover:bg-gray-50/50"
                      }`}
                    >
                      <div className="flex justify-between items-center gap-4">
                        {/* Left: Camera name & location */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-gray-800 text-blue-400" : "bg-gray-100 text-blue-600"}`}>
                            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                              />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className={`font-bold text-sm truncate ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{camera.camera_name}</span>
                            <span className={`text-[10px] truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{camera.location_name}</span>
                          </div>
                        </div>
                        {/* Right: Percentage + Status (fixed-width coverage column keeps % aligned) */}
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="w-[5.5rem] text-right flex items-baseline justify-end gap-1.5">
                            <span className={`text-xs font-black ${isDarkMode ? "text-white" : "text-gray-900"}`}>{camera.coverage}</span>
                            <span className={`text-[9px] uppercase font-bold tracking-tighter whitespace-nowrap ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Coverage</span>
                          </div>
                          <span
                            className={`inline-block text-[9px] px-2 py-1 rounded-lg font-black uppercase tracking-wider whitespace-nowrap ${
                              camera.status === "Looking Good"
                                ? isDarkMode
                                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                  : "bg-emerald-100 text-emerald-700"
                                : camera.status === "Needs Improvement"
                                  ? isDarkMode
                                    ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                                    : "bg-amber-100 text-amber-700"
                                  : isDarkMode
                                    ? "bg-red-500/10 text-red-400 border border-red-500/20"
                                    : "bg-red-100 text-red-700"
                            }`}
                          >
                            {camera.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: ${isDarkMode ? "#374151" : "#CBD5E1"};
          border-radius: 20px;
        }
        `}
      </style>
    </div>
  );
};

export default CleaningCoverageBars;
